import {
  EventBusInterceptor,
  Interceptor,
  UseService,
  ApiServiceHandler
} from '@haskoning/js-api-authentication-management'

import API from '@/api/apiUrls'
import { useEventsBus } from '@haskoning/vue-component-library'

export const { emit } = useEventsBus()

type AuthenticationExpiredHandler = () => void | Promise<void>

let authenticationExpiredHandler: AuthenticationExpiredHandler | undefined
let authenticationExpiredPromise: Promise<void> | null = null

export const setAuthenticationExpiredHandler = (handler: AuthenticationExpiredHandler) => {
  authenticationExpiredHandler = handler
}

const getErrorStatus = (error: unknown) => {
  if (error instanceof Response) return error.status
  if (!error || typeof error !== 'object' || !('status' in error)) return null
  return typeof error.status === 'number' ? error.status : null
}

const isTokenAuthenticationError = (error: unknown) => {
  const status = getErrorStatus(error)
  if (status === 401) return true
  if (status !== 403 || !error || typeof error !== 'object' || !('statusText' in error)) {
    return false
  }

  const statusText = error.statusText
  if (!statusText || typeof statusText !== 'object') return false

  const details = statusText as {
    code?: unknown
    detail?: unknown
    messages?: unknown
  }
  if (details.code === 'token_not_valid') return true
  if (typeof details.detail === 'string' && details.detail.toLowerCase().includes('token')) {
    return true
  }
  if (!Array.isArray(details.messages)) return false

  return details.messages.some((message) => {
    if (!message || typeof message !== 'object' || !('message' in message)) return false
    const text = message.message
    return (
      typeof text === 'string' &&
      (text.toLowerCase().includes('expired') || text.toLowerCase().includes('invalid'))
    )
  })
}

const handleAuthenticationExpired = async () => {
  if (!authenticationExpiredHandler) return
  if (!authenticationExpiredPromise) {
    authenticationExpiredPromise = Promise.resolve(authenticationExpiredHandler()).finally(() => {
      authenticationExpiredPromise = null
    })
  }
  await authenticationExpiredPromise
}

class CloudComputeEventBusInterceptor extends EventBusInterceptor {
  private readonly emitEvent: typeof emit

  constructor() {
    super(emit)
    this.emitEvent = emit
  }

  override async afterError(response: unknown) {
    if (isTokenAuthenticationError(response)) {
      await handleAuthenticationExpired()
    }

    if (!(response instanceof Response)) {
      return super.afterError(response)
    }

    this.emitEvent('loading', false)
    try {
      // Keep the original response body available to the caller so forms can
      // map API validation errors back to their fields.
      const error = await response.clone().json()
      const statusText =
        error && typeof error === 'object' && typeof error.detail === 'string'
          ? error.detail
          : error
      this.emitEvent('error', { statusText, status: response.status })
    } catch {
      this.emitEvent('error', response)
    }
    this.emitEvent('afterError', response)
    return Promise.reject(response)
  }
}

const interceptor = new CloudComputeEventBusInterceptor()

const baseURL = import.meta.env.VITE_APP_API_BASE_URL
const apiServiceHandler = new ApiServiceHandler(
  baseURL,
  API.TOKEN.ACCESS,
  API.TOKEN.REFRESH,
  interceptor
)
const silentApiServiceHandler = new ApiServiceHandler(
  baseURL,
  API.TOKEN.ACCESS,
  API.TOKEN.REFRESH,
  new Interceptor()
)

export const clearApiAuthentication = () => {
  apiServiceHandler.headers.delete('Authorization')
  silentApiServiceHandler.headers.delete('Authorization')
}

export const useApi = () => {
  const api = new UseService(apiServiceHandler)
  return { api }
}

export const useSilentApi = () => {
  const api = new UseService(silentApiServiceHandler)
  return { api }
}
